
import { IOrderClause } from '../api/types/IOrderClause';

// Look at VizClient

export interface OrderClausesDirection {
  asc: 'asc';
  desc: 'desc';
}

export class OrderClauses extends Array<IOrderClause> {

  public static Direction: OrderClausesDirection = {
    asc: 'asc',
    desc: 'desc'
  };

  public static DefaultMaxLength: number = 3;

  private maxLength: number;

  public constructor(orderClauses?: IOrderClause[], maxLength: number = OrderClauses.DefaultMaxLength) {
    super();
    this.maxLength = maxLength;

    if (!orderClauses) {
      return;
    }

    orderClauses.some((clause: IOrderClause) => {
      if (this.length >= maxLength) {
        return true; // break the loop if length is at the maxLength
      }

      if (!clause || this.some(existingClause => existingClause.field === clause.field)) {
        return false; // Skip this clause
      }

      this.push({
        field: clause.field,
        ascending: clause.ascending
      });

      return false; // Keep iterating
    });
  }

  public prependClause(newClause: IOrderClause): OrderClauses {
    return new OrderClauses([newClause].concat(this), this.maxLength);
  }

  public prependAscField(field: string): OrderClauses {
    return this.prependClause({ field, ascending: true });
  }

  public prependDescField(field: string): OrderClauses {
    return this.prependClause({ field, ascending: false });
  }

  public withoutField(field: string): OrderClauses {
    let filteredClauses = this.filter(clause => clause.field !== field);
    return new OrderClauses(filteredClauses, this.maxLength);
  }

  public withMaxLength(maxLength: number): OrderClauses {
    return new OrderClauses(this, maxLength);
  }

  public startsWithField(field: string): boolean {
    return (this.length > 0) && (this[0].field === field);
  }

  public findOrderClauseForField(field: string): IOrderClause {
    return this.filter(clause => clause.field === field)[0] || null;
  }

  public filterFields(possibleFields: string[]): OrderClauses {
    // Create a map to reduce operational time in the following filter
    let possibleFieldsMap = possibleFields.reduce(
      (fieldMap, field) => {
        fieldMap[field] = true;
        return fieldMap;
      },
      <{[field: string]: boolean}> {}
    );
    let filteredClauses = this.filter(clause => possibleFieldsMap[clause.field]);
    return new OrderClauses(filteredClauses);
  }

  private static serializeClause(clause: IOrderClause): string {
    let ascendingString = clause.ascending ? OrderClauses.Direction.asc : OrderClauses.Direction.desc;
    return `${clause.field}:${ascendingString}`;
  }

  public static serialize(orderClauses: IOrderClause[]): string {
    return orderClauses.map(OrderClauses.serializeClause).join(',');
  }

  public toString(): string {
    return OrderClauses.serialize(this);
  }

  private static deserializeClause(input: string): IOrderClause | void {
    let parts = input.split(':');

    if (parts.length !== 2) {
      return;
    }

    return {
      field: parts[0], // TODO: validate field value
      ascending: parts[1] === OrderClauses.Direction.asc
    };
  }

  private static isIOrderClause(clause: IOrderClause | void): clause is IOrderClause {
    return !!clause;
  }

  public static deserialize(input: string): OrderClauses | void {
    if (!input) {
      return; // TODO: or throw?
    }

    return new OrderClauses(
      input.split(',')
           .map(OrderClauses.deserializeClause)
           .filter(OrderClauses.isIOrderClause)
           // Needed until https://github.com/Microsoft/TypeScript/issues/7657 is closed
           .map(x => <IOrderClause> x)
    );
  }
}
